import { canvasElements, childrenOf, isContainer, listElements } from '@inkadra/canvas-core';
import { type Element, PLAN_ASPECT, readElement, roots, UNSORTED } from '@inkadra/doc-schema';
import * as Y from 'yjs';
import { croppedRotatedSize } from './image-crop.ts';

/** Transaction origin of edits made in this tab: the undo history tracks only these. */
export const LOCAL = Symbol('local');

export interface Snapshot {
  byId: ReadonlyMap<string, Element>;
  /** Elements on the canvas, in drawing order. */
  canvas: readonly Element[];
  /** Children of each column and scene, top to bottom. */
  columns: ReadonlyMap<string, readonly Element[]>;
  unsorted: readonly Element[];
}

const EMPTY: Snapshot = { byId: new Map(), canvas: [], columns: new Map(), unsorted: [] };

/**
 * Plain view of a board document for React. Rebuilt after each Yjs transaction; element
 * objects keep their identity when they did not change, so memoised cards skip rendering.
 */
export class BoardStore {
  readonly undo: Y.UndoManager;
  private snapshot: Snapshot = EMPTY;
  private listeners = new Set<() => void>();
  private sizeListeners = new Set<() => void>();
  /** Rendered heights of elements whose height follows their content. */
  private heights = new Map<string, number>();
  private sizeVersion = 0;
  /** Elements that just appeared, with the time: their cards pop in once (takeAdded). */
  private added = new Map<string, number>();

  constructor(readonly doc: Y.Doc) {
    const { elements } = roots(doc);
    this.undo = new Y.UndoManager(elements, {
      trackedOrigins: new Set([LOCAL]),
      captureTimeout: 400,
    });
    this.rebuild();
    elements.observeDeep(this.onChange);
  }

  destroy() {
    roots(this.doc).elements.unobserveDeep(this.onChange);
    this.undo.destroy();
  }

  private onChange = (events: Array<Y.YEvent<Y.AbstractType<unknown>>>) => {
    const { elements } = roots(this.doc);
    const touched = new Set<string>();
    for (const event of events) {
      if (event.target === elements) {
        for (const key of event.changes.keys.keys()) touched.add(key);
        continue;
      }
      const [id, field] = event.path;
      // Rich text changes inside a note do not change the plain view.
      if (typeof id === 'string' && field !== 'body') touched.add(id);
    }
    if (touched.size === 0) return;
    const byId = new Map(this.snapshot.byId);
    let changed = false;
    const added: string[] = [];
    for (const id of touched) {
      const map = elements.get(id);
      const next = map ? readElement(map) : null;
      const old = byId.get(id);
      if (!next) {
        changed = byId.delete(id) || changed;
      } else if (!old || !sameElement(old, next)) {
        if (!old) added.push(id);
        byId.set(id, next);
        changed = true;
      }
    }
    if (!changed) return;
    // Not the first load of a board, nor a large sync after being offline: those just appear.
    const local = events[0]?.transaction.local ?? false;
    if (added.length > 0 && added.length <= 50 && (local || this.snapshot.byId.size > 0)) {
      const now = performance.now();
      for (const [id, at] of this.added) if (now - at > 1000) this.added.delete(id);
      for (const id of added) this.added.set(id, now);
    }
    this.index(byId);
    for (const l of this.listeners) l();
  };

  private rebuild() {
    const byId = new Map<string, Element>();
    for (const el of listElements(this.doc)) byId.set(el.id, el);
    this.index(byId);
  }

  private index(byId: Map<string, Element>) {
    const all = [...byId.values()];
    const columns = new Map<string, Element[]>();
    for (const el of all) {
      if (isContainer(el)) columns.set(el.id, childrenOf(all, el.id));
    }
    this.snapshot = {
      byId,
      canvas: canvasElements(all),
      columns,
      unsorted: childrenOf(all, UNSORTED),
    };
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => this.snapshot;

  /** True once for an element added in the last second, for its card to pop in. */
  takeAdded(id: string): boolean {
    const at = this.added.get(id);
    this.added.delete(id);
    return at !== undefined && performance.now() - at < 1000;
  }

  subscribeSizes = (listener: () => void) => {
    this.sizeListeners.add(listener);
    return () => this.sizeListeners.delete(listener);
  };

  getSizeVersion = () => this.sizeVersion;

  setHeight(id: string, height: number) {
    if (Math.abs((this.heights.get(id) ?? -1) - height) < 0.5) return;
    this.heights.set(id, height);
    this.sizeVersion += 1;
    for (const l of this.sizeListeners) l();
  }

  /** Height of an element on the canvas, measured or estimated. */
  heightOf(el: Element): number {
    // A character's height follows its content, whatever height an older document stored.
    if (
      el.type === 'link' ||
      el.type === 'column' ||
      el.type === 'scene' ||
      el.type === 'character'
    )
      return this.heights.get(el.id) ?? estimateHeight(el);
    return el.h ?? this.heights.get(el.id) ?? estimateHeight(el);
  }

  rectOf(el: Element) {
    return { x: el.x, y: el.y, w: el.w, h: this.heightOf(el) };
  }

  /** The Y.Map behind an element, for rich text and to-do items. */
  map(id: string): Y.Map<unknown> | undefined {
    return roots(this.doc).elements.get(id);
  }
}

function estimateHeight(el: Element): number {
  switch (el.type) {
    case 'image': {
      const dims = croppedRotatedSize(4, 3, el.crop, el.rotation);
      return Math.round((el.w * dims.height) / dims.width);
    }
    case 'column':
    case 'scene':
      return 120;
    case 'frame':
      return Math.round(el.w * 0.56) + 48;
    case 'color':
      return el.w;
    case 'board':
      return 66;
    case 'todo':
      return 48 + el.items.length * 26;
    case 'section':
      return 44;
    case 'table':
      return el.rows.length * 31 + 2;
    case 'plan':
      return Math.round(el.w / PLAN_ASPECT) + 34;
    case 'map':
      return el.imageAssetId ? Math.round((el.w * 560) / 1024) + 52 : 52;
    default:
      return 64;
  }
}

function sameElement(a: Element, b: Element): boolean {
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  for (const k of ka) {
    const va = (a as Record<string, unknown>)[k];
    const vb = (b as Record<string, unknown>)[k];
    if (va === vb) continue;
    if (typeof va === 'object' && JSON.stringify(va) === JSON.stringify(vb)) continue;
    return false;
  }
  return true;
}
